import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChalkboardTeacher, faCommentDots, faClipboardCheck, faCircleCheck, faGlobe } from '@fortawesome/free-solid-svg-icons';
import Classes from './Classes';
import Pending from './Pending';
import Evaluation from './Evaluation';
import FeedbackApprove from './FeedbackApprove';
import './MyClasses.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const TABS = [
  { key: 'classes', label: 'Classes', icon: faChalkboardTeacher },
  { key: 'pending', label: 'Pending feedback', icon: faCommentDots },
  { key: 'evaluation', label: 'Evaluation', icon: faClipboardCheck },
  { key: 'feedbackapprove', label: 'Feedback approve', icon: faCircleCheck },
];

const DAYS = [
  ['yesterday', 'Yesterday'],
  ['today', 'Today'],
  ['tomorrow', 'Tomorrow'],
];

/* Same date as before: the day in LEBANON time (the old code added +3h to UTC).
   Done with the Beirut time zone so it is right on any computer. */
const beirutDate = (offsetDays) => {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Beirut', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
};
const dayToDate = (day) => (day === 'yesterday' ? beirutDate(-1) : day === 'tomorrow' ? beirutDate(1) : beirutDate(0));

const AdminMyClasses = ({ userData }) => {
  // Tab kept in the address (?tab=pending) so other pages can link to it
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'classes';

  const [day, setDay] = useState('today');
  const [refreshKey, setRefreshKey] = useState(0);
  const [counts, setCounts] = useState({ pendingFeedbacks: 0, nbOfEvaluations: 0, nbofapprove: 0, nbofdemo: 0, nbofpaid: 0 });

  const adminId = userData?.id;

  // Your time zone (all class times on this page are shown in it)
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetMin = -new Date().getTimezoneOffset();
  const offsetLabel = `UTC${offsetMin >= 0 ? '+' : '-'}${String(Math.floor(Math.abs(offsetMin) / 60)).padStart(2, '0')}:${String(
    Math.abs(offsetMin) % 60
  ).padStart(2, '0')}`;

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'classes') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  // ---------- Badge counts (same endpoints + same rule), every minute ----------
  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/myclasses/${dayToDate(day)}`, auth());
      let evalCount = 0;
      try {
        const ev = await axios.get(`${API}/api/admin/adminfeedback/evaluations`, auth());
        // only evaluations that are NOT full pay
        evalCount = (ev.data?.evaluations || []).filter((e) => e.calculatedFullpay !== 0).length;
      } catch (err) {
        console.error('Error fetching evaluation count:', err);
      }
      setCounts({ ...res.data, nbOfEvaluations: evalCount });
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  }, [day]);

  useEffect(() => {
    if (!adminId) return undefined;
    fetchCounts();
    const id = setInterval(fetchCounts, 60000);
    return () => clearInterval(id);
  }, [adminId, fetchCounts]);

  const changeDay = (d) => {
    setDay(d);
    setRefreshKey((k) => k + 1); // same as before: reload the class list
  };

  const badge = {
    classes: (counts.nbofpaid || 0) + (counts.nbofdemo || 0),
    pending: counts.pendingFeedbacks || 0,
    evaluation: counts.nbOfEvaluations || 0,
    feedbackapprove: counts.nbofapprove || 0,
  };

  if (!userData) {
    return (
      <div className="rmamc" aria-busy="true">
        <div className="rmamc-inner">
          <div className="rmamc-skel rmamc-skel--title" />
          <div className="rmamc-skel rmamc-skel--bar" />
          <div className="rmamc-skel rmamc-skel--body" />
        </div>
      </div>
    );
  }

  return (
    <div className="rmamc">
      <div className="rmamc-inner">
        <header className="rmamc-head">
          <div>
            <span className="rmamc-eyebrow">Classes</span>
            <h1 className="rmamc-title">
              Classes <span>management</span>
            </h1>
          </div>
          <p className="rmamc-tz">
            <FontAwesomeIcon icon={faGlobe} /> {timeZone.replace(/_/g, ' ')} ({offsetLabel})
          </p>
        </header>

        <div className="rmamc-bar">
          <div className="rmamc-tabs" role="tablist" aria-label="Classes sections">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                id={`rmamc-tab-${t.key}`}
                aria-selected={tab === t.key}
                aria-controls="rmamc-panel"
                className={`rmamc-tab ${tab === t.key ? 'is-on' : ''}`}
                onClick={() => setTab(t.key)}
              >
                <FontAwesomeIcon icon={t.icon} />
                <span>{t.label}</span>
                {badge[t.key] > 0 && (
                  <span className={`rmamc-badge ${t.key === 'classes' ? 'is-info' : ''}`} aria-label={`${badge[t.key]}`}>
                    {badge[t.key]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {tab === 'classes' && (
            <div className="rmamc-days" role="radiogroup" aria-label="Day">
              {DAYS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={day === value}
                  className={day === value ? 'is-on' : ''}
                  onClick={() => changeDay(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div id="rmamc-panel" role="tabpanel" aria-labelledby={`rmamc-tab-${tab}`} className="rmamc-panel">
          {/* same components + same props as before */}
          {tab === 'classes' && <Classes key={refreshKey} userData={userData} day={day} />}
          {tab === 'pending' && <Pending onSubmitrefresh={fetchCounts} userData={userData} />}
          {tab === 'evaluation' && <Evaluation userData={userData} />}
          {tab === 'feedbackapprove' && <FeedbackApprove userData={userData} onSubmitrefresh={fetchCounts} />}
        </div>
      </div>
    </div>
  );
};

export default AdminMyClasses;